import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import sswhiteLogo from "../../assets/images/sswhite.png";
import shuklaLogo from "../../assets/images/shukla.png";
import sssLogo from "../../assets/images/sss.png";

// Rounds 6 batch — Document Branding (approved Q8): the DIR header carries a
// selectable brand logo plus the shared company address on every branding.
export const DIR_ADDRESS_LINE = "8300 Sheen Dr, St. Petersburg, FL 33709";
export const BRAND_TITLES = {
  sswhite: "S.S. White Technologies",
  shukla: "Shukla Medical",
  sss: "Shukla Surgical Support",
};
const BRAND_LOGO_SRC = { sswhite: sswhiteLogo, shukla: shuklaLogo, sss: sssLogo };

/** Load the selected brand's PNG as {dataUrl,w,h} for jsPDF.addImage.
 *  Resolves null on ANY failure so the PDF still generates without a logo. */
export function loadBrandLogo(branding) {
  const src = BRAND_LOGO_SRC[branding] || BRAND_LOGO_SRC.sswhite;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve({ dataUrl: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Round 62 — Digital Inspection Record PDF, legacy layout.
 *
 * Reproduces the legacy Crystal Reports "Print Mafia Table" output (see the
 * legacy screenshot: header band "S.S. White Technologies Digital Inspection
 * Record" + address, Job/Part/Rev + Quantity/Part Name header grid, then ONE
 * SECTION PER DNA: a boxed spec strip (TYPE / MIN / NOMINAL / MAX / UNIT /
 * GAUGE / WI ATP NUMBER), the AddlInfo tolerance line, an "InProcess" entries
 * block and a "Q-Final" entries block (# / Piece / Value / By / On), footer
 * "MAFIA FIR REV-J" + "Page X of Y").
 *
 * Same approach as the EPO purchase-order PDF (src/pages/ExpensePurchaseOrder/
 * epoToPdf.js): client-side jsPDF + jspdf-autotable, already in package.json —
 * no backend service, no new dependency.
 *
 * Input: the SAME reportData object the DIR preview modal already renders
 * (built in PIR.js), so the PDF is always exactly what the user previewed.
 */

const M = 44; // page margin (pt)

const fmtDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return (
    d.toLocaleDateString("en-US") +
    "  " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" })
  );
};

const s = (v) => (v == null ? "" : String(v));

// Group entry rows by DNANum, ordered by CellNum then piecenumber.
function groupEntriesByDna(rows) {
  const map = new Map();
  (rows || []).forEach((r) => {
    const dna = s(r.DNANum ?? r.dnaNum).trim();
    if (!map.has(dna)) map.set(dna, []);
    map.get(dna).push(r);
  });
  for (const list of map.values()) {
    list.sort(
      (a, b) =>
        (Number(a.CellNum ?? a.cellNum ?? 0) || 0) -
          (Number(b.CellNum ?? b.cellNum ?? 0) || 0) ||
        (Number(a.piecenumber ?? 0) || 0) - (Number(b.piecenumber ?? 0) || 0)
    );
  }
  return map;
}

function entriesBody(list) {
  return (list || []).map((r, i) => [
    String(i + 1),
    s(r.piecenumber ?? r.PieceNumber ?? r.pieceNumber),
    s(r.Value ?? r.value),
    // Round MAFIA-UAT-3 (2026) — PDF audit: legacy fed the Tool column
    // (the measuring instrument used) into InspectionRecord.rpt; the
    // modern DIR was dropping it. Restored between Value and By.
    s(r.Tool ?? r.tool),
    s(r.EnteredBy ?? r.enteredBy),
    fmtDateTime(r.enteredTime ?? r.EnteredTime),
  ]);
}

const ENTRY_COLS = [
  { header: "#", dataKey: 0 },
  { header: "Piece", dataKey: 1 },
  { header: "Value", dataKey: 2 },
  { header: "Tool", dataKey: 3 },
  { header: "By", dataKey: 4 },
  { header: "On", dataKey: 5 },
];

export function buildDirDoc(reportData) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - M * 2;

  // ── Header band (legacy: right-aligned company title + address) ──────────
  const headerDrawn = new Set();
  const drawPageHeader = () => {
    const pageNo = doc.internal.getCurrentPageInfo().pageNumber;
    if (headerDrawn.has(pageNo)) return;
    headerDrawn.add(pageNo);
    // Rounds 6 batch (final, owner request) — TEXT-ONLY branding: the branded
    // title with the shared company address directly below it. No logo image.
    const brandTitle =
      BRAND_TITLES[(reportData && reportData.branding) || "sswhite"] || BRAND_TITLES.sswhite;
    // Owner request — the COMPANY header prints LARGER than everything
    // else (18pt), with the document name and address beneath it.
    doc.setFont("helvetica", "bolditalic");
    doc.setFontSize(18);
    doc.text(brandTitle, M, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Digital Inspection Record", M, 46);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(DIR_ADDRESS_LINE, M, 58);
  };

  drawPageHeader();

  // ── Job header grid (legacy bordered box) ────────────────────────────────
  autoTable(doc, {
    startY: 72,
    margin: { left: M, right: M },
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 5,
      lineColor: [0, 0, 0],
      lineWidth: 0.75,
      textColor: [0, 0, 0],
    },
    body: [
      [
        { content: "Job #", styles: { fontStyle: "bold", halign: "right", cellWidth: 60 } },
        { content: s(reportData.jobNumber), styles: { cellWidth: 110 } },
        { content: "Part #:", styles: { fontStyle: "bold", halign: "right", cellWidth: 55 } },
        { content: s(reportData.partNum) },
        { content: `Rev:  ${s(reportData.revText)}`, styles: { cellWidth: 90 } },
      ],
      [
        { content: "Quantity:", styles: { fontStyle: "bold", halign: "right" } },
        { content: s(reportData.quantity) },
        { content: "Part Name:", styles: { fontStyle: "bold", halign: "right" } },
        { content: s(reportData.partName), colSpan: 2 },
      ],
    ],
  });

  let y = doc.lastAutoTable.finalY + 14;

  // ── Per-DNA sections ─────────────────────────────────────────────────────
  const inProcByDna = groupEntriesByDna(reportData.operatorEntriesRows);
  const qFinalByDna = groupEntriesByDna(reportData.qFinalEntriesRows);

  const instructions = reportData.instructionRows || [];

  const ensureRoom = (needed) => {
    if (y + needed > pageH - 60) {
      doc.addPage();
      drawPageHeader();
      y = 76;
    }
  };

  instructions.forEach((ins) => {
    const dna = s(ins.DNANum).trim();
    ensureRoom(120); // don't start a section a few points before the footer

    // DNA label (legacy: small number on the box's top-left edge)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(dna, M + 4, y + 2);

    // Boxed spec strip
    autoTable(doc, {
      startY: y + 5,
      margin: { left: M, right: M, top: 76 },
      didDrawPage: () => drawPageHeader(),
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: 8.5,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        textColor: [0, 0, 0],
      },
      headStyles: { fontStyle: "bold" },
      tableLineColor: [0, 0, 0],
      tableLineWidth: 1,
      head: [["TYPE", "MIN", "NOMINAL", "MAX", "UNIT", "GAUGE", "WI ATP NUMBER"]],
      body: [
        [
          s(ins.DimType),
          s(ins.Min),
          s(ins.Nominal),
          s(ins.Max),
          s(ins.UnitMeasurement),
          s(ins.SpecialGages),
          s(ins.WIATPNum),
        ],
        // Legacy prints the AddlInfo tolerance line inside the box.
        ...(s(ins.AddlInfo).trim()
          ? [[{ content: s(ins.AddlInfo), colSpan: 7, styles: { fontSize: 8 } }]]
          : []),
        ...(s(ins.SpecialInstr).trim()
          ? [[{ content: `Special Instructions: ${s(ins.SpecialInstr)}`, colSpan: 7, styles: { fontSize: 8 } }]]
          : []),
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.16 },
        1: { cellWidth: contentW * 0.1 },
        2: { cellWidth: contentW * 0.11 },
        3: { cellWidth: contentW * 0.1 },
        4: { cellWidth: contentW * 0.09 },
        5: { cellWidth: contentW * 0.18 },
        6: { cellWidth: contentW * 0.26 },
      },
    });
    y = doc.lastAutoTable.finalY + 8;

    // InProcess / Q-Final entry blocks (legacy order)
    const blocks = [
      { title: "InProcess", rows: inProcByDna.get(dna) || [], comment: s(ins.InProcCom) },
      { title: "Q-Final", rows: qFinalByDna.get(dna) || [], comment: s(ins.QCCom) },
    ];

    blocks.forEach((blk) => {
      ensureRoom(46);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(blk.title, M + 2, y + 8);
      y += 12;

      autoTable(doc, {
        startY: y,
        margin: { left: M + 24, right: M + 24, top: 76 },
        theme: "plain",
        styles: {
          font: "helvetica",
          fontSize: 8.5,
          cellPadding: { top: 1.5, bottom: 1.5, left: 4, right: 4 },
          textColor: [0, 0, 0],
        },
        headStyles: { fontStyle: "bold" },
        columns: ENTRY_COLS,
        body: entriesBody(blk.rows),
        columnStyles: {
          0: { cellWidth: 34 },
          1: { cellWidth: 56 },
          2: { cellWidth: 100 },
          3: { cellWidth: 90 },   // Tool (Round MAFIA-UAT-3)
          4: { cellWidth: 110 },
        },
        // Repeat the header band when an entries table crosses a page.
        didDrawPage: () => drawPageHeader(),
      });
      y = doc.lastAutoTable.finalY + 4;

      if (blk.comment) {
        ensureRoom(14);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.text(`Comment: ${blk.comment}`, M + 26, y + 8, {
          maxWidth: contentW - 52,
        });
        y += 14;
      }
      y += 4;
    });

    y += 8; // gap before the next DNA section
  });

  // ── Footer on every page (legacy: MAFIA FIR REV-J + Page X of Y) ────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("MAFIA FIR REV-J", M, pageH - 30);
    doc.text(`Page ${i} of ${pageCount}`, pageW - M, pageH - 30, { align: "right" });
  }

  return doc;
}

/** Open the legacy-format DIR PDF in a new tab (view / print / save).
 *  Text-only branding (owner request) — no image preload needed. */
export function openDirPdf(reportData) {
  const doc = buildDirDoc(reportData);
  const url = doc.output("bloburl");
  window.open(url, "_blank");
}

/** Download the legacy-format DIR PDF. */
export function downloadDirPdf(reportData) {
  const doc = buildDirDoc(reportData);
  const job = s(reportData.jobNumber).replace(/[^A-Za-z0-9._-]/g, "_") || "job";
  const part = s(reportData.partNum).replace(/[^A-Za-z0-9._-]/g, "_") || "part";
  doc.save(`DIR_${job}_${part}.pdf`);
}
